import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { FiEye, FiEyeOff, FiArrowLeft } from 'react-icons/fi';
import { redirectByRole } from '../../utils/roleRedirect';
import { getStoredAuthUser, hasStoredAuthSession } from '../../utils/authStorage';
import { Button } from '../../components/ui/button';
import { LoginCarousel } from './LoginCarousel';
import { GoogleLogin } from '@react-oauth/google';
import { FaMicrosoft } from 'react-icons/fa';
import ethosboardLogo from '../../assets/images/ethoslogo.png';
import useTenant from '../../hooks/useTenant'; // [TENANT]
import '../../styles/tailwind.css';
import '../../styles/tenant-login.css'; // [TENANT]

// [TENANT] Brand colours for a client subdomain. useTenant() sets
// --org-primary / --org-accent on the page; these are used inline because this
// page's Tailwind classes (primary-600 etc.) are fixed EthosBoard colours.
const TENANT_PRIMARY = { color: 'var(--org-primary, #4F46E5)' };
const TENANT_BUTTON = {
  background:
    'linear-gradient(135deg, var(--org-primary, #4F46E5) 0%, var(--org-accent, #4338CA) 100%)',
  color: '#fff',
};

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false); // [TENANT]

  const { login, loginWithGoogle, loading, isAuthenticated, user, completeAuthSession } = useAuth();
  const navigate = useNavigate();

  // [TENANT] igi.ethosboard.com -> onTenant = true and tenant = { name, logo, colours }
  const { onTenant, tenant, loading: tenantLoading, notFound, logoUrl, loginSlides } = useTenant();

  // [TENANT] Client logo on a client subdomain, EthosBoard logo everywhere else
  const showTenantLogo = onTenant && Boolean(logoUrl) && !logoFailed;
  const brandName = onTenant && tenant?.name ? tenant.name : null;

  // [TENANT] Browser tab title follows the organization
  useEffect(() => {
    if (!brandName) return undefined;
    const previousTitle = document.title;
    document.title = `Sign in | ${brandName}`;
    return () => {
      document.title = previousTitle;
    };
  }, [brandName]);

  // [TENANT] Browser tab icon follows the organization logo
  useEffect(() => {
    if (!showTenantLogo) return undefined;

    let link = document.querySelector("link[rel~='icon']");
    const created = !link;
    const previousHref = link ? link.href : null;

    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = logoUrl;

    return () => {
      if (created) {
        link.remove();
      } else if (previousHref) {
        link.href = previousHref;
      }
    };
  }, [showTenantLogo, logoUrl]);

  // TEMPORARY: remove before shipping
  const handleDummyMasterAdminLogin = () => {
    const fakeUser = { id: 0, name: 'Master Admin', email: 'dev@ethosboard.test', role: 3 };
    const fakeTokens = { access_token: 'dev-dummy-token' };

    // completeAuthSession is already exposed by your AuthContext
    completeAuthSession({ user: fakeUser, tokens: fakeTokens });
    redirectByRole(fakeUser.role, navigate);
  };

  useEffect(() => {
    if (loading) return;
    const sessionUser = user || getStoredAuthUser();
    if ((isAuthenticated || hasStoredAuthSession()) && sessionUser) {
      redirectByRole(sessionUser.role, navigate);
    }
  }, [isAuthenticated, loading, user, navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const result = await login(formData.email, formData.password);

      if (result.success && result.data?.next_step === 'SETUP_2FA') {
        navigate('/2fa/setup', { replace: true });
        return;
      }
      if (result.success && result.data?.next_step === 'VERIFY_OTP') {
        navigate('/2fa/verify', { replace: true });
        return;
      }
      if (result.success && result.data?.user) {
        redirectByRole(result.data.user.role, navigate);
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsSubmitting(true);
    try {
      const result = await loginWithGoogle(credentialResponse.credential);
      if (result.success && result.data?.next_step === 'SETUP_2FA') {
        navigate('/2fa/setup', { replace: true });
        return;
      }
      if (result.success && result.data?.next_step === 'VERIFY_OTP') {
        navigate('/2fa/verify', { replace: true });
        return;
      }
      if (result.success && result.data?.user) {
        redirectByRole(result.data.user.role, navigate);
      }
    } catch (error) {
      console.error('Google login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsSubmitting(true);
    try {
      // MSAL is downloaded only now, when the user clicks the button
      const { msalInstance, loginRequest } = await import('../../config/msalConfig');
      await msalInstance.initialize();
      await msalInstance.loginRedirect(loginRequest);
      // Browser navigates away here. MicrosoftRedirectHandler.jsx catches the return trip
    } catch (error) {
      console.error('Microsoft login error:', error);
      setIsSubmitting(false);
    }
  };

  if (isAuthenticated && !loading) return null;

  // [TENANT] Looking up the client for this subdomain: show a blank screen
  // instead of flashing the EthosBoard logo first.
  if (onTenant && tenantLoading) {
    return <div className="tw-scope min-h-screen w-full bg-auth" />;
  }

  // [TENANT] Subdomain doesn't match any organization (nope.ethosboard.com)
  if (onTenant && notFound) {
    return (
      <div className="tw-scope flex min-h-screen w-full flex-col items-center justify-center bg-auth px-6 text-center">
        <img src={ethosboardLogo} alt="EthosBoard" className="mb-8 h-10 w-auto object-contain" />
        <h1 className="font-display text-2xl font-bold text-gray-900">Organization not found</h1>
        <p className="mt-2 max-w-md text-sm text-gray-500">
          We couldn&apos;t find an organization at this address. Check the link you were given,
          or contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className={`tw-scope flex min-h-screen w-full bg-auth ${onTenant ? 'tenant-theme' : ''}`}>
      {/* Left: form */}
      <div className="flex w-full flex-col justify-center px-8 py-10 md:w-[45%] lg:px-14">
        <div className="mb-10 flex items-center gap-2">
          {showTenantLogo ? (
            <img
              src={logoUrl}
              alt={`${brandName || 'Organization'} logo`}
              onError={() => setLogoFailed(true)}
              className="h-12 w-auto max-w-[260px] object-contain"
            />
          ) : brandName ? (
            // Client has no logo (or it failed to load): show the name as text
            <span className="text-2xl font-bold text-gray-900">{brandName}</span>
          ) : (
            <img src={ethosboardLogo} alt="EthosBoard Logo" className="h-10 w-auto object-contain" />
          )}
        </div>

        <p
          className="text-sm font-semibold text-primary-600"
          style={onTenant ? TENANT_PRIMARY : undefined}
        >
          Welcome Back
        </p>
        <h1 className="mt-1 font-display text-3xl font-bold text-gray-900">
          Sign in to your account
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          {brandName ? `Sign in to ${brandName} to continue` : 'Sign in to your account to continue'}
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-900">
              Email Address
            </label>
            <div className={cnInput(errors.email)}>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
                disabled={isSubmitting || loading}
                className="w-full bg-transparent text-sm outline-none placeholder:text-gray-500"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-xs text-red-600">{errors.email}</p>
            )}
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-900">Password</label>
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-primary-600 hover:underline"
                style={onTenant ? TENANT_PRIMARY : undefined}
              >
                Forgot Password?
              </Link>
            </div>
            <div className={cnInput(errors.password)}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                disabled={isSubmitting || loading}
                className="w-full bg-transparent text-sm outline-none placeholder:text-gray-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isSubmitting || loading}
                className="text-gray-500 hover:text-primary-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-red-600">{errors.password}</p>
            )}
          </div>

          <Button
            type="submit"
            variant="premium"
            size="lg"
            className="w-full"
            style={onTenant ? TENANT_BUTTON : undefined}
            disabled={isSubmitting || loading}
          >
            {isSubmitting || loading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        {/* [TENANT] Dev shortcut: never on a client subdomain, and never in a production build */}
        {!onTenant && process.env.NODE_ENV !== 'production' && (
          <button type="button" onClick={handleDummyMasterAdminLogin} className="mt-4 text-xs text-gray-400 underline">
            Dev: Sign in as Master Admin
          </button>
        )}

        <div className="mt-3 flex items-center gap-3">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-xs text-gray-400">OR CONTINUE WITH</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        <div className="mt-3 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => console.error('Google Sign-In failed')}
          />
          <button
            type="button"
            onClick={handleMicrosoftLogin}
            disabled={isSubmitting || loading}
            className="tenant-social flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:w-auto"
          >
            <FaMicrosoft className="text-[#00A4EF]" size={16} />
            Sign in with Microsoft
          </button>
        </div>

        {/* [TENANT] The marketing home page belongs to the main site only */}
        {!onTenant && (
          <Link
            to="/"
            className="mt-4 flex items-center justify-center gap-1 text-sm font-medium text-primary-600 hover:underline"
          >
            <FiArrowLeft size={14} /> Back to Home
          </Link>
        )}
      </div>

      {/* Right: same carousel for every organization; only the name and theme colours change */}
      <LoginCarousel brandName={brandName} slides={loginSlides} />
    </div>
  );
};

// small helper so the two inputs share the same wrapper classes
const cnInput = (hasError) =>
  `auth-input ${hasError ? 'auth-input-error' : ''}`;

export default Login;
